import { format } from "date-fns";
import { type BookingFormSchema } from "./validation";
import { type BookingFormProps } from ".";
import { type Booking } from "@/bookings/Booking";
import { removeNothings } from "@/lib/utils/arrays";
import { nullable } from "@/lib/utils/common";
import { toJsDate } from "@/lib/utils/dates";

const MAX_BOOKING_NAME_LENGTH = 50;
const DUPLICATE_NAME_SUFFIX = " (copy)";

/**
 * Maps a booking from the API to the values `BookingForm` expects.
 */
export const mapBookingToFormData = (booking: Booking): BookingFormProps["initialData"] => {
  if (!booking) return undefined;

  return {
    // Basic booking fields
    name: booking.name || "",
    status: booking.status || "DRAFT",
    plannedShipmentDate: booking.plannedShippingDate ? toJsDate(booking.plannedShippingDate) : new Date(),
    numberOfHead: booking.numberOfHead || undefined,
    note: booking.note || "",
    destination: booking.destination?.id || null,
    businessAccountManagerId: booking.accountManager?.user?.id?.toString() || null,
    source: booking.source?.id || null,

    // Metadata fields
    ACCOUNT_NUMBER: booking.accountNumber || "",
    ACCREDITATIONS: booking.accreditations?.map((acc) => acc.id) || [],
    BRAND_NOTE: booking.brandNote || "",
    BREED_NOTE: booking.breedNote || "",
    BROKER: booking.broker?.id || undefined,
    CERTIFICATE_NUMBER: booking.certificateNumber || "",
    CONFIRMED: booking.confirmed || false,
    DATE_DELIVERED: booking.dateDelivered ? toJsDate(booking.dateDelivered) : undefined,
    DATE_OF_SALE: booking.dateOfSale ? toJsDate(booking.dateOfSale) : undefined,
    ESTIMATED_CHOICE_PERCENT: booking.estimatedChoicePercent || undefined,
    ESTIMATED_WEIGHT_NOTE: booking.estimatedWeightNote || "",
    FREIGHT_COST: booking.freightCost || "",
    GRID: booking.grid?.id || undefined,
    LOT_NUMBER: booking.lotNumber || "",
    PEN_NUMBER: booking.penNumber || "",
    PRICE: booking.price || "",
    PROGRAM: booking.program?.id || undefined,
    PURCHASE_PRICE_PER_CWT: booking.purchasePricePerCwt || "",
    RISK_MANAGEMENT: booking.riskManagement || false,
    SERVICE_PROVIDED: booking.serviceProvided || "",
    SEX_CLASS: booking.sexClass || undefined,
    SHIPPED_WEIGHT: booking.shippedWeight || undefined,
    SHRINK_PERCENT: booking.shrinkPercent || undefined,
    SLIDE: booking.slide?.toString() || "",
    SOURCE_NOTE: booking.sourceNote || "",
    TRUCKING_COMPANY: booking.truckingCompany?.id || undefined,
    VETERINARIAN: booking.veterinarian?.user?.id || undefined,
    BASE_WEIGHT: booking.baseWeight || undefined,
    LABELS: booking.labels?.map((label) => label.id) || [],
    DATE_OF_VISIT: booking.dateOfVisit ? toJsDate(booking.dateOfVisit) : undefined,
    CONDITION: booking.condition || undefined,
    CONDITIONS: removeNothings(booking?.conditions || []),
    OWNERSHIP_TYPE: nullable(booking.ownershipType) || undefined,
    PROJECTED_OUT_DATE: booking.projectedOutDate ? toJsDate(booking.projectedOutDate) : undefined,
  };
};

/**
 * Adds the "(copy)" suffix, trimming the original name so the result stays within
 * the 50 characters the name field allows.
 */
const toDuplicateName = (name: string) =>
  `${name.slice(0, MAX_BOOKING_NAME_LENGTH - DUPLICATE_NAME_SUFFIX.length).trimEnd()}${DUPLICATE_NAME_SUFFIX}`;

/**
 * Maps a booking to form values for a new copy of it: every field is kept except
 * the name, which gains a "(copy)" suffix, and the status, which starts as DRAFT.
 */
export const toDuplicateFormData = (booking: Booking): BookingFormProps["initialData"] => {
  const formData = mapBookingToFormData(booking);
  if (!formData) return undefined;

  return {
    ...formData,
    name: toDuplicateName(formData.name ?? ""),
    status: "DRAFT",
  };
};

/**
 * Maps form values to the booking fields shared by the create and update mutation inputs.
 */
export const toBookingInputFields = (values: BookingFormSchema) => ({
  // Basic booking fields
  name: values.name,
  status: values.status,
  plannedShippingDate: format(values.plannedShipmentDate, "yyyy-MM-dd"),
  numberOfHead: values.numberOfHead,
  note: nullable(values.note),
  destination: nullable(values.destination),
  source: nullable(values.source),
  accountManager: nullable(values.businessAccountManagerId),
  // Metadata fields
  accountNumber: nullable(values.ACCOUNT_NUMBER),
  accreditations: values.ACCREDITATIONS?.length ? values.ACCREDITATIONS : [],
  brandNote: nullable(values.BRAND_NOTE),
  breedNote: nullable(values.BREED_NOTE),
  broker: nullable(values.BROKER),
  certificateNumber: nullable(values.CERTIFICATE_NUMBER),
  confirmed: values.CONFIRMED,
  dateDelivered: values.DATE_DELIVERED ? format(values.DATE_DELIVERED, "yyyy-MM-dd") : null,
  dateOfSale: values.DATE_OF_SALE ? format(values.DATE_OF_SALE, "yyyy-MM-dd") : null,
  estimatedChoicePercent: !!values.ESTIMATED_CHOICE_PERCENT ? Number(values.ESTIMATED_CHOICE_PERCENT) : null,
  estimatedWeightNote: nullable(values.ESTIMATED_WEIGHT_NOTE),
  freightCost: nullable(values.FREIGHT_COST),
  grid: nullable(values.GRID),
  lotNumber: nullable(values.LOT_NUMBER),
  penNumber: nullable(values.PEN_NUMBER),
  price: nullable(values.PRICE),
  program: nullable(values.PROGRAM),
  purchasePricePerCwt: nullable(values.PURCHASE_PRICE_PER_CWT),
  riskManagement: values.RISK_MANAGEMENT,
  serviceProvided: nullable(values.SERVICE_PROVIDED),
  sexClass: nullable(values.SEX_CLASS),
  shippedWeight: !!values.SHIPPED_WEIGHT ? Number(values.SHIPPED_WEIGHT) : null,
  shrinkPercent: !!values.SHRINK_PERCENT ? Number(values.SHRINK_PERCENT) : null,
  slide: values.SLIDE ? Number(values.SLIDE) : null,
  sourceNote: nullable(values.SOURCE_NOTE),
  truckingCompany: nullable(values.TRUCKING_COMPANY),
  veterinarian: nullable(values.VETERINARIAN),
  baseWeight: !!values.BASE_WEIGHT ? Number(values.BASE_WEIGHT) : null,
  labels: values.LABELS?.length ? values.LABELS : [],
  dateOfVisit: values.DATE_OF_VISIT ? format(values.DATE_OF_VISIT, "yyyy-MM-dd") : null,
  condition: nullable(values.CONDITION),
  conditions: values.CONDITIONS?.length ? values.CONDITIONS : [],
  ownershipType: nullable(values.OWNERSHIP_TYPE),
  projectedOutDate: values.PROJECTED_OUT_DATE ? format(values.PROJECTED_OUT_DATE, "yyyy-MM-dd") : null,
});
